import { validateReadingEvidenceConfig, type ReadingEvidenceConfig } from './ReadingEvidenceConfig.js';

export type ReadingEvidenceCheckResult =
  | 'not-read'
  | 'incomplete'
  | 'claim-mismatch'
  | 'evidence-mismatch'
  | 'complete';

export interface ReadingEvidenceSerializedState {
  readonly version: 1;
  readonly pageIndex: number;
  readonly visitedPageIndexes: readonly number[];
  readonly selectedClaimId: string | null;
  readonly selectedEvidenceIds: readonly string[];
  readonly hintStage: number;
  readonly attempts: number;
  readonly complete: boolean;
}

export class ReadingEvidenceState {
  private pageIndex = 0;
  private readonly visitedPageIndexes = new Set<number>([0]);
  private selectedClaimId: string | null = null;
  private readonly selectedEvidenceIds = new Set<string>();
  private hintStage = 0;
  private attempts = 0;
  private completed = false;

  constructor(
    readonly config: ReadingEvidenceConfig,
    saved?: Partial<ReadingEvidenceSerializedState>,
  ) {
    validateReadingEvidenceConfig(config);
    if (saved?.version === 1) this.restore(saved);
  }

  setPage(index: number) {
    if (this.completed || !Number.isInteger(index) || index < 0 || index >= this.config.pages.length) return false;
    this.pageIndex = index;
    this.visitedPageIndexes.add(index);
    return true;
  }

  selectClaim(claimId: string) {
    if (this.completed || !this.config.claimChoices.some(choice => choice.id === claimId)) return false;
    this.selectedClaimId = claimId;
    return true;
  }

  toggleEvidence(evidenceId: string) {
    if (this.completed || !this.config.evidenceDetails.some(detail => detail.id === evidenceId)) return false;
    if (this.selectedEvidenceIds.has(evidenceId)) this.selectedEvidenceIds.delete(evidenceId);
    else this.selectedEvidenceIds.add(evidenceId);
    return true;
  }

  check(): ReadingEvidenceCheckResult {
    this.attempts += 1;
    if (this.visitedPageIndexes.size !== this.config.pages.length) return 'not-read';
    if (!this.selectedClaimId || this.selectedEvidenceIds.size < this.config.requiredEvidenceIds.length) return 'incomplete';
    if (this.selectedClaimId !== this.config.correctClaimId) return 'claim-mismatch';
    const selected = [...this.selectedEvidenceIds].sort();
    const required = [...this.config.requiredEvidenceIds].sort();
    if (selected.length !== required.length || selected.some((id, index) => id !== required[index])) {
      return 'evidence-mismatch';
    }
    this.completed = true;
    return 'complete';
  }

  requestHint() {
    this.hintStage = Math.min(this.config.hints.length, this.hintStage + 1);
    return this.config.hints[this.hintStage - 1];
  }

  resetSelections() {
    if (this.completed) return false;
    this.selectedClaimId = null;
    this.selectedEvidenceIds.clear();
    this.hintStage = 0;
    return true;
  }

  serialize(): ReadingEvidenceSerializedState {
    return {
      version: 1,
      pageIndex: this.pageIndex,
      visitedPageIndexes: [...this.visitedPageIndexes],
      selectedClaimId: this.selectedClaimId,
      selectedEvidenceIds: [...this.selectedEvidenceIds],
      hintStage: this.hintStage,
      attempts: this.attempts,
      complete: this.completed,
    };
  }

  get snapshot() {
    return {
      ...this.serialize(),
      allPagesRead: this.visitedPageIndexes.size === this.config.pages.length,
      pageCount: this.config.pages.length,
    };
  }

  private restore(saved: Partial<ReadingEvidenceSerializedState>) {
    const validPage = (value: unknown): value is number => Number.isInteger(value)
      && Number(value) >= 0 && Number(value) < this.config.pages.length;
    (saved.visitedPageIndexes ?? []).filter(validPage).forEach(index => this.visitedPageIndexes.add(index));
    this.pageIndex = validPage(saved.pageIndex) ? saved.pageIndex : 0;
    this.visitedPageIndexes.add(this.pageIndex);
    this.selectedClaimId = this.config.claimChoices.some(choice => choice.id === saved.selectedClaimId)
      ? saved.selectedClaimId ?? null : null;
    const validEvidence = new Set(this.config.evidenceDetails.map(detail => detail.id));
    (saved.selectedEvidenceIds ?? []).forEach(id => {
      if (validEvidence.has(id)) this.selectedEvidenceIds.add(id);
    });
    this.hintStage = Math.min(this.config.hints.length, Math.max(0, Number(saved.hintStage) || 0));
    this.attempts = Math.max(0, Number(saved.attempts) || 0);
    const selected = [...this.selectedEvidenceIds].sort();
    const required = [...this.config.requiredEvidenceIds].sort();
    this.completed = saved.complete === true
      && this.selectedClaimId === this.config.correctClaimId
      && selected.length === required.length
      && selected.every((id, index) => id === required[index]);
  }
}
